import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { peekCurrentUser, userCanSeePrices } from "@/lib/auth";
import { searchHeroArticles, type HeroArticleSuggestion } from "@/lib/material";
import { isStundenMaterialUnit } from "@/lib/materialUnits";

/** Anzeigename in Stundennachweisen: Artikelname, nicht die Hero-Beschreibung (Preishistorie). */
export function timesheetMaterialDisplayName(article: HeroArticleSuggestion): string {
  const name = (article.hero_base_name || "").trim();
  if (name) return name;

  const description = (article.hero_base_description || "").trim();
  const label = (article.label || "").trim();
  if (label && label !== description) return label;

  return (article.hero_nr || article.value || label || "").trim();
}

function withTimesheetMaterialLabel(article: HeroArticleSuggestion): HeroArticleSuggestion {
  const label = timesheetMaterialDisplayName(article);
  return { ...article, label };
}

export function manualMaterialSuggestion(label: string): HeroArticleSuggestion {
  const text = label.trim();
  return {
    label: text,
    value: text,
    artikel_id: null,
    hero_nr: null,
    hero_unit: null,
    einheit: null,
    ean: null,
  };
}

function formatMaterialPrice(value: number | null | undefined): string | null {
  if (value == null || Number.isNaN(Number(value))) return null;
  return `${new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value))} €`;
}

export function materialToOption(
  article: HeroArticleSuggestion,
): AutocompleteOption<HeroArticleSuggestion> {
  const normalized = withTimesheetMaterialLabel(article);
  const key = normalized.artikel_id
    ? `hero:${normalized.artikel_id}`
    : `manual:${normalized.label}`;
  const parts: string[] = [];
  if (normalized.hero_nr) parts.push(`Hero ${normalized.hero_nr}`);
  if (userCanSeePrices(peekCurrentUser())) {
    const price = formatMaterialPrice(normalized.verkaufspreis_netto);
    if (price) parts.push(price);
  }
  return {
    key,
    label: normalized.label,
    description: parts.length ? parts.join(" · ") : undefined,
    value: normalized,
  };
}

export function manualMaterialToOption(label: string): AutocompleteOption<HeroArticleSuggestion> {
  return materialToOption(manualMaterialSuggestion(label));
}

export function materialRecordToOption(form: {
  text: string;
  artikel_id?: number | null;
  hero_nr?: string | null;
  hero_unit?: string | null;
  einheit?: string | null;
  ean?: string | null;
}): AutocompleteOption<HeroArticleSuggestion> | null {
  const trimmed = form.text.trim();
  if (!trimmed) return null;

  if (form.artikel_id) {
    return materialToOption({
      label: trimmed,
      value: trimmed,
      artikel_id: form.artikel_id,
      hero_nr: form.hero_nr ?? null,
      hero_unit: form.hero_unit ?? null,
      einheit: form.einheit ?? null,
      ean: form.ean ?? null,
    });
  }

  return manualMaterialToOption(trimmed);
}

export function isHeroMaterialOption(
  opt: AutocompleteOption<HeroArticleSuggestion> | null,
): boolean {
  return Boolean(opt?.value?.artikel_id);
}

/** @deprecated Use materialRecordToOption */
export function findMaterialOption(text: string): AutocompleteOption<HeroArticleSuggestion> | null {
  return materialRecordToOption({ text });
}

export type TimesheetMaterialSourceOptions = {
  /** Nur Artikel mit Einheit Stunden / Std. vorschlagen. */
  stundenOnly?: boolean;
};

export async function timesheetMaterialSource(
  query: string,
  options?: TimesheetMaterialSourceOptions,
): Promise<AutocompleteOption<HeroArticleSuggestion>[]> {
  const list = await searchHeroArticles(query, {
    limit: options?.stundenOnly ? 50 : 20,
  });
  const mapped = list.map((article) => materialToOption(article));
  if (!options?.stundenOnly) return mapped;
  return mapped.filter((opt) =>
    isStundenMaterialUnit(opt.value.einheit || opt.value.hero_unit),
  );
}

export function isHeroMaterialRecord(material: {
  artikel_id?: number | null;
  hero_nr?: string | null;
  source_type?: string | null;
}): boolean {
  return Boolean(material.artikel_id || (material.source_type === "hero" && material.hero_nr));
}

/** Festpreis aus App-Angebot / AB – nur Menge editierbar. */
export function isAngebotMaterialRecord(material: {
  gd_position_id?: number | null;
  source_type?: string | null;
}): boolean {
  const source = String(material.source_type || "").trim().toLowerCase();
  return (
    material.gd_position_id != null ||
    source === "gd_position" ||
    source === "festpreis" ||
    source === "angebot" ||
    source === "angebot_position"
  );
}

/** „Angebot“ oder „Auftragsbestätigung“ aus Dokumentart oder fertigem Label. */
export function dokumentArtLabel(quelleTypOrLabel: string | null | undefined): string {
  const raw = String(quelleTypOrLabel || "").trim();
  const key = raw.toLowerCase();
  if (key === "auftragsbestaetigung" || key === "auftragsbestätigung") {
    return "Auftragsbestätigung";
  }
  if (raw === "Auftragsbestätigung") return raw;
  return "Angebot";
}

/** Badge-Text z. B. „gem. Angebot AN26-0006“ oder „gem. Auftragsbestätigung AB26-0004“. */
export function angebotMaterialBadgeLabel(material: {
  bemerkung?: string | null;
  source_document_nummer?: string | null;
  quelle_typ?: string | null;
}): string | null {
  const bemerkung = String(material.bemerkung || "").trim();
  if (/^gem\.\s+/i.test(bemerkung)) return bemerkung;
  const fromDoc = String(material.source_document_nummer || "").trim();
  if (!fromDoc) return null;
  return formatAngebotMaterialBadge(fromDoc, material.quelle_typ);
}

export function formatAngebotMaterialBadge(
  docNummer: string | null | undefined,
  quelleTypOrLabel?: string | null,
): string | null {
  const nummer = String(docNummer || "").trim();
  if (!nummer) return null;
  if (/^gem\.\s+/i.test(nummer)) return nummer;
  return `gem. ${dokumentArtLabel(quelleTypOrLabel)} ${nummer}`;
}
